import { EmailIcon } from '#/shared/ui/icons'
import { CallablePhone } from '#/shared/ui/phone-number'

interface CustomerCellProps {
  name?: string
  nameHref?: string
  onNameClick?: () => void
  nameAccessory?: React.ReactNode
  email?: string
  phone?: string
  /** Rendered next to the phone number — e.g. a deliverability badge. */
  phoneAccessory?: React.ReactNode
  company?: string
  /** Shipment this customer belongs to — links the SMS to its history. */
  shipmentId?: string
  /** Customer record id — maps the SMS recipient to its customer. */
  customerId?: string
}

function CustomerName({
  name,
  nameHref,
  onNameClick,
}: {
  name: string
  nameHref?: string
  onNameClick?: () => void
}) {
  const isLink = !!nameHref || !!onNameClick

  if (!isLink) {
    return (
      <span title={name} className="max-w-44 truncate text-sm text-gray-900">
        {name}
      </span>
    )
  }

  const className =
    'max-w-44 truncate cursor-pointer text-sm text-primary hover:underline'

  if (nameHref) {
    return (
      <a href={nameHref} title={name} className={className}>
        {name}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={onNameClick}
      title={name}
      className={className}
    >
      {name}
    </button>
  )
}

export function CustomerCell({
  name,
  nameHref,
  onNameClick,
  nameAccessory,
  email,
  phone,
  phoneAccessory,
  company,
  shipmentId,
  customerId,
}: CustomerCellProps) {
  return (
    <div className="flex flex-col items-start">
      {name && (
        <div className="flex h-7 items-center gap-2">
          <CustomerName
            name={name}
            nameHref={nameHref}
            onNameClick={onNameClick}
          />
          {nameAccessory}
        </div>
      )}
      {email && (
        <div className="flex h-7 items-center gap-1.25">
          <EmailIcon className="size-4.5 shrink-0 text-primary" />
          <span
            title={email}
            className="max-w-44 truncate text-sm text-gray-900"
          >
            {email}
          </span>
        </div>
      )}
      {phone && (
        <div className="flex h-7 items-center gap-1.25">
          <CallablePhone
            value={phone}
            className="max-w-44 text-sm text-gray-900"
            smsTarget={{ phone, name, shipmentId, customerId }}
          />
          {phoneAccessory}
        </div>
      )}
      {company && (
        <div className="flex h-7 items-center">
          <span
            title={company}
            className="max-w-44 truncate text-sm text-gray-500"
          >
            {company}
          </span>
        </div>
      )}
    </div>
  )
}
